/**
 * @component UserProfile
 * @description The profile: who you are, and four short groups of settings.
 *
 * It was ten cards down one page, seven screens tall on a phone, saving five
 * different ways: two Save buttons labelled differently, choices that saved
 * with a toast, choices that saved in silence, names that saved when the box
 * lost focus. Saving one card could also throw away what was typed in
 * another.
 *
 * Now it is one list. Each row says its current answer, so the settings can
 * be read without opening anything, and opens a screen of its own. One rule
 * holds on every screen: what is tapped is kept at once, with "Saved" beside
 * it; what is typed is kept with the one Save at the foot of its screen.
 *
 * The open screen is in the address (?s=details), so the phone's Back closes
 * it and a link can open one directly.
 *
 * Under Subscription, for a gardener with the photo AI, the "Photo help this
 * month" bar (PhotoHelpBar, D23): the share of the allowance used, never
 * dollars. A member tending someone else's garden has no Subscription row
 * there (useTendingFor; B23: the invitee only uses the inviter's plan). Its
 * Details row says the garden follows its owner's place, not a ZIP "that sets
 * your weather" (B37).
 *
 * Under Garden, Seed orders by email (D69, features/seed-orders): the garden's private address for forwarding seed
 * orders; its row says the address once its screen has read it (the server makes it then).
 *
 * Route: /profile, inside Layout (signed in). The ?s= values are the Screen
 * names below; anything else shows the list.
 *
 * Used by: App.tsx. Each screen lives in ./components and is mounted here
 * all at once, closed; only the one named in ?s= is open.
 *
 * Surprising: the profile picture's address is built from the server's limits.SPACES_BASE_URL
 * and the stored file name (the bucket is public), falling back to the
 * sign-in provider's photo, here and in ProfilePhotoCard alike.
 *
 * @module UserProfile
 */

import React from 'react';
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Plus } from 'lucide-react';
import { UserAvatar } from '@/shared/components/layout/UserAvatar';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { languages } from '@/shared/i18n/i18nSetup';
import { linksOf } from '@/shared/lib/socialLinks';
import { selectableStages } from '@/shared/lib/stages';
import { getStages } from '@/shared/api/stages';
import { getNoteShortcuts } from '@/shared/api/noteShortcuts';
import { getAlertPreferences } from '@/shared/api/alerts';
import { getGardenPeople } from '@/shared/api/gardenMembers';
import { textVariants } from '@/shared/styles/variants';
import { ProfileDetailsScreen } from './components/ProfileDetailsForm';
import { ProfileLinksScreen } from './components/ProfileLinksCard';
import { PublicPageScreen } from './components/PublicPageCard';
import { PreferencesScreen } from './components/ProfilePreferences';
import { ComingUpScreen } from './components/ComingUpScreen';
import { useComingUpDays } from './components/useComingUpDays';
import { WarningsScreen } from './components/WarningsCard';
import { MondayEmailScreen } from './components/MondayEmailScreen';
import { getMondayEmail } from '@/shared/api/gardenEmail';
import { ALERT_PREFERENCES_KEY, GARDEN_PEOPLE_KEY, MONDAY_EMAIL_KEY } from './components/profileQueries';
import { GardenPeopleScreen } from './components/GardenPeopleCard';
import { LabelsScreen } from './components/LabelsScreen';
import { AccountScreen } from './components/AccountCard';
import { AppVersion } from './components/AppVersion';
import { SeedOrdersScreen } from '@/features/seed-orders/SeedOrdersScreen';
import { SEED_ORDERS_MINE_KEY } from '@/features/seed-orders/lib/seedOrderQueries';
import { getSeedOrdersMine } from '@/shared/api/seedOrders';
import { PhotoHelpBar } from '@/features/billing/components/PhotoHelpBar';
import { useTendingFor } from '@/features/billing/lib/useBilling';
import { STAGES_QUERY_KEY } from '@/config/tunables/ui';
import { NOTE_SHORTCUTS_QUERY_KEY } from '@/config/tunables/garden';

/** The profile's screens, by the name each has in the address (?s=). */
type Screen = 'details' | 'links' | 'public' | 'people' | 'labels' | 'warnings' | 'monday' | 'seeds' | 'comingup' | 'prefs' | 'account';
/** Every screen, to check an address's ?s= against. */
const SCREENS: Screen[] = ['details', 'links', 'public', 'people', 'labels', 'warnings', 'monday', 'seeds', 'comingup', 'prefs', 'account'];

/** A row's look, shared by the button and link kinds. */
const ROW = 'flex min-h-[3.5rem] w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none';

/** One row of the list: what it is, its current answer, and a › to open it. */
const Row: React.FC<{ title: string; sub?: string; onOpen?: () => void; to?: string; danger?: boolean }> = ({ title, sub, onOpen, to, danger }) => {
  const inner = (
    <>
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[0.9375rem] font-medium', danger && 'text-destructive')}>{title}</span>
        {sub && <span className="block truncate text-sm text-muted-foreground">{sub}</span>}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </>
  );
  return to
    ? <Link to={to} className={ROW}>{inner}</Link>
    : <button type="button" onClick={onOpen} className={ROW}>{inner}</button>;
};

/** A titled box of rows. */
const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-1.5">
    <h2 className="font-body px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
    <div className="divide-y overflow-hidden rounded-lg border bg-card">{children}</div>
  </section>
);

/** The profile page: who you are, then the groups of rows, each opening its screen. */
export const UserProfile: React.FC = () => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { mysqlUser, currentUser } = useAuth();
  const { userConfig: { preferences } } = useUserConfig();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  /** Someone else's garden: the owner's plan, so no Subscription row (B23). */
  const tending = useTendingFor() !== null;

  const asked = params.get('s') as Screen | null;
  const openScreen: Screen | null = asked && SCREENS.includes(asked) ? asked : null;

  // Opened from the list, a screen is a step forward, so Back — the phone's
  // or the screen's own — returns to the list. Arrived at directly, closing
  // it simply shows the list.
  /** Opens a screen, as a step forward from the list. */
  const open = (screen: Screen): void => navigate({ search: `?s=${screen}` }, { state: { fromList: true } });
  /** Closes the open screen: Back when it was opened from the list, else just the list. */
  const close = (): void => {
    if ((location.state as { fromList?: boolean } | null)?.fromList) navigate(-1);
    else navigate({ search: '' }, { replace: true });
  };

  // The answers the rows show. Small, cached, and the same queries the
  // screens read, so opening one finds them already there.
  const { data: stages = [] } = useQuery({ queryKey: [STAGES_QUERY_KEY, language], queryFn: () => getStages(language) });
  const { data: shortcuts = [] } = useQuery({ queryKey: [NOTE_SHORTCUTS_QUERY_KEY], queryFn: getNoteShortcuts });
  const { data: alerts } = useQuery({ queryKey: ALERT_PREFERENCES_KEY, queryFn: getAlertPreferences });
  const { data: mondayEmail } = useQuery({ queryKey: MONDAY_EMAIL_KEY, queryFn: getMondayEmail });
  const { data: people } = useQuery({ queryKey: GARDEN_PEOPLE_KEY, queryFn: getGardenPeople });
  // Seed orders by email (D69): read only when its screen opens (the server makes the address then), so the row says
  // the address once it is known and nothing before.
  const { data: seeds } = useQuery({ queryKey: SEED_ORDERS_MINE_KEY, queryFn: getSeedOrdersMine, enabled: false });

  const links = linksOf(mysqlUser?.profile_links);
  const linksSub = links.length === 0
    ? t('profile.index.linksNone')
    : links.length === 1
      ? t(`profile.links.${links[0].key}`)
      : t('profile.index.linksMore', { first: t(`profile.links.${links[0].key}`), count: links.length - 1 });
  const members = people?.members.length ?? 0;
  const warningsSub = !alerts
    ? undefined
    : alerts.byEmail && alerts.bySms
      ? t('profile.index.warningsBoth')
      : alerts.byEmail ? t('profile.index.warningsEmail') : alerts.bySms ? t('profile.index.warningsText') : t('profile.index.warningsOff');
  const comingUp = useComingUpDays();
  const prefsSub = [
    languages[preferences.language],
    preferences.temperatureUnit === 'C' ? '°C' : '°F',
    t(`profile.preferences.theme.option.${preferences.theme}`),
  ].join(' · ');

  return (
    // data-clarity-mask: the whole profile (name, email, postcode, links) is hidden from session replays.
    <div data-clarity-mask="true" className={cn('mx-auto max-w-2xl space-y-6', textVariants({ font: 'serif' }))}>
      {/* Who you are, first: the picture and name, opening the screen they
          are changed on. */}
      <button
        type="button"
        onClick={() => open('details')}
        className="flex w-full items-center gap-4 rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="relative h-14 w-14 shrink-0 rounded-full bg-muted">
          <UserAvatar mysqlUser={mysqlUser} firebaseUser={currentUser} className="h-full w-full text-xl" />
          {/* The + says there is a menu for the photo behind this row, with or without a photo. */}
          <span aria-hidden="true" className="absolute bottom-0 right-0 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-card">
            <Plus className="h-3.5 w-3.5" />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-lg font-semibold">{mysqlUser?.display_name || t('profile.index.noName')}</span>
          <span className="block truncate text-sm text-muted-foreground">{mysqlUser?.email}</span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      <Group title={t('profile.index.you')}>
        <Row
          title={t('profile.index.details')}
          sub={tending ? t('profile.index.detailsTending') : mysqlUser?.postal_code ? t('profile.index.detailsZip', { zip: mysqlUser.postal_code }) : t('profile.index.detailsNoZip')}
          onOpen={() => open('details')}
        />
        <Row title={t('profile.links.title')} sub={linksSub} onOpen={() => open('links')} />
        <Row
          title={t('profile.public.title')}
          sub={mysqlUser?.new_plants_public ? t('profile.index.publicOn') : t('profile.index.publicOff')}
          onOpen={() => open('public')}
        />
      </Group>

      <Group title={t('profile.index.garden')}>
        <Row
          title={t('profile.people.title')}
          sub={people ? (members === 0 ? t('profile.index.peopleNone') : members === 1 ? t('profile.index.peopleOne') : t('profile.index.peopleCount', { count: members })) : undefined}
          onOpen={() => open('people')}
        />
        <Row
          title={t('profile.index.labels')}
          sub={t('profile.index.labelsCount', { stages: selectableStages(stages).length, shortcuts: shortcuts.length })}
          onOpen={() => open('labels')}
        />
        <Row title={t('profile.warnings.title')} sub={warningsSub} onOpen={() => open('warnings')} />
        {/* Its own row: switching it off never touches warning emails. */}
        <Row
          title={t('profile.monday.title')}
          sub={mondayEmail ? t(`profile.monday.${mondayEmail.frequency}`) : undefined}
          onOpen={() => open('monday')}
        />
        {/* D69: the garden's private address for seed orders; on Free, that it comes with Grow More. */}
        <Row
          title={t('profile.index.seeds')}
          sub={seeds?.address ?? (seeds && !seeds.allowed ? t('profile.index.seedsFree') : undefined)}
          onOpen={() => open('seeds')}
        />
      </Group>

      <Group title={t('profile.index.app')}>
        {/* How far ahead Today's Coming up looks: its own row, not inside Weather warnings. */}
        <Row title={t('profile.index.comingUp')} sub={comingUp.shown === undefined ? undefined : t('profile.index.comingUpSub', { days: comingUp.words(comingUp.shown) })} onOpen={() => open('comingup')} />
        <Row title={t('profile.index.prefs')} sub={prefsSub} onOpen={() => open('prefs')} />
      </Group>

      <Group title={t('profile.index.account')}>
        {!tending && <Row title={t('profile.index.subscription')} sub={t('profile.index.subscriptionHint')} to="/subscribe" />}
        {/* How much of the photo help is used (subscribers, founders, trials): here and nowhere else. */}
        <PhotoHelpBar />
        <Row title={t('profile.index.accountRow')} sub={t('profile.index.accountHint')} onOpen={() => open('account')} />
      </Group>

      {/* Last, quiet, and readable aloud when a deploy is in question. */}
      <AppVersion />

      <ProfileDetailsScreen open={openScreen === 'details'} onClose={close} />
      <ProfileLinksScreen open={openScreen === 'links'} onClose={close} />
      <PublicPageScreen open={openScreen === 'public'} onClose={close} />
      <GardenPeopleScreen open={openScreen === 'people'} onClose={close} />
      <LabelsScreen open={openScreen === 'labels'} onClose={close} />
      <WarningsScreen open={openScreen === 'warnings'} onClose={close} />
      <MondayEmailScreen open={openScreen === 'monday'} onClose={close} />
      <SeedOrdersScreen open={openScreen === 'seeds'} onClose={close} />
      <ComingUpScreen open={openScreen === 'comingup'} onClose={close} />
      <PreferencesScreen open={openScreen === 'prefs'} onClose={close} />
      <AccountScreen open={openScreen === 'account'} onClose={close} />
    </div>
  );
};
